/* global Modal */

if (!customElements.get('special-order-modal')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class SpecialOrderModal extends Modal {
      constructor() {
        super();
        this.pendingForm = null;
        this.pendingSubmitBtn = null;
        this.productData = null;

        this.titleEl = this.querySelector('.js-special-order-title');
        this.leadTimeEl = this.querySelector('.js-special-order-lead-time');
        this.conditionsEl = this.querySelector('.js-special-order-conditions');
        this.conditionsBox = this.querySelector('.special-order-modal__box--conditions');
        this.confirmBtn = this.querySelector('.js-special-order-confirm');

        this.bindEvents();
        this.loadProductData();
        SpecialOrderModal.bindFormInterception(this);
      }

      bindEvents() {
        this.confirmBtn.addEventListener('click', this.handleConfirm.bind(this));

        document.addEventListener('special-order-modal:open', this.handleExternalOpen.bind(this));
      }

      loadProductData() {
        const dataEl = this.querySelector('.js-special-order-data');
        if (!dataEl) return;

        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (error) {
          this.productData = null;
        }
      }

      /**
       * Intercepts add-to-cart on product forms within the same section.
       */
      static bindFormInterception() {
        if (SpecialOrderModal.interceptionBound) return;
        SpecialOrderModal.interceptionBound = true;

        document.addEventListener('submit', (evt) => {
          const form = evt.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!form.matches('.js-product-form-main')) return;
          if (form.dataset.specialOrderConfirmed === 'true') {
            delete form.dataset.specialOrderConfirmed;
            return;
          }

          const modal = document.querySelector('special-order-modal');
          if (!modal || !modal.productData) return;

          const orderInfo = modal.getOrderInfoForForm(form);
          if (!orderInfo) return;

          evt.preventDefault();
          evt.stopImmediatePropagation();

          modal.openForForm(form, orderInfo);
        }, true);
      }

      /**
       * Gets modal type for the selected variant.
       * @param {HTMLFormElement} form - Product form element.
       * @returns {?object}
       */
      getOrderInfoForForm(form) {
        if (!this.productData) return null;

        const variantId = Number(form.querySelector('[name="id"]')?.value);
        if (!variantId) return null;

        const variant = this.productData.variants.find((item) => item.id === variantId);
        if (!variant) return null;

        return SpecialOrderModal.getOrderInfoForVariant(variant, this.productData);
      }

      /**
       * Mirrors product-inventory.js backorder detection.
       * @param {object} variant - Variant inventory data.
       * @param {object} productData - Product modal data.
       * @returns {?object}
       */
      static getOrderInfoForVariant(variant, productData) {
        const isBackordered = variant.inventory_quantity <= 0
          && variant.inventory_policy === 'continue';

        if (!isBackordered) return null;

        if (productData.hasPreOrder && productData.preOrderEnabled) {
          return { type: 'pre_order' };
        }

        if (productData.hasSpecialOrder && productData.specialOrderEnabled) {
          return { type: 'special_order' };
        }

        return null;
      }

      /**
       * Returns content config for the active order type.
       * @param {string} type - pre_order or special_order.
       * @returns {?object}
       */
      getContentForType(type) {
        if (!this.productData) return null;
        return type === 'pre_order'
          ? this.productData.preOrder
          : this.productData.specialOrder;
      }

      /**
       * Opens the modal for a pending form submission.
       * @param {HTMLFormElement} form - Product form element.
       * @param {object} orderInfo - Active order type.
       */
      openForForm(form, orderInfo) {
        this.pendingForm = form;
        this.pendingSubmitBtn = form.querySelector('[name="add"]');
        this.populateContent(orderInfo);
        this.open(this.pendingSubmitBtn);
      }

      /**
       * Opens the modal via custom event (for external triggers).
       * @param {CustomEvent} evt - Event object.
       */
      handleExternalOpen(evt) {
        const { form, type } = evt.detail || {};
        const orderInfo = { type: type || (this.productData?.hasPreOrder ? 'pre_order' : 'special_order') };

        if (form) {
          this.openForForm(form, orderInfo);
        } else {
          this.populateContent(orderInfo);
          this.open();
        }
      }

      /**
       * Updates modal content for the active order type.
       * @param {object} orderInfo - Active order type.
       */
      populateContent(orderInfo) {
        const content = this.getContentForType(orderInfo.type);
        if (!content) return;

        if (this.titleEl) {
          this.titleEl.textContent = content.title || '';
        }

        const introEl = this.querySelector('.js-special-order-intro');
        if (introEl) {
          introEl.textContent = content.introText || '';
        }

        const leadTimeHeadingEl = this.querySelector('.js-special-order-lead-time-heading');
        if (leadTimeHeadingEl) {
          leadTimeHeadingEl.textContent = content.leadTimeHeading || '';
        }

        if (this.leadTimeEl) {
          this.leadTimeEl.textContent = content.leadTimeBody || '';
        }

        const conditionsHeadingEl = this.querySelector('.js-special-order-conditions-heading');
        if (conditionsHeadingEl) {
          conditionsHeadingEl.textContent = content.conditionsHeading || '';
        }

        if (this.conditionsEl) {
          this.conditionsEl.textContent = content.conditionsText || '';
          this.conditionsBox?.classList.toggle('hidden', !content.conditionsText);
        }
      }

      handleConfirm() {
        if (!this.pendingForm) return;

        const form = this.pendingForm;
        form.dataset.specialOrderConfirmed = 'true';
        this.close();
        form.requestSubmit();
      }

      /**
       * Clears pending state when modal closes.
       */
      close() {
        super.close();
        this.pendingForm = null;
        this.pendingSubmitBtn = null;
      }
    }

    customElements.define('special-order-modal', SpecialOrderModal);
  });
}
